import { useCallback, useSyncExternalStore } from "react";

import { legacyKeyOf, readWithLegacyFallback } from "./legacyStorage";

/**
 * Petits réglages d'affichage retenus d'un écran à l'autre et d'une session à
 * l'autre, jusqu'au prochain vidage du cache navigateur : panneaux repliés,
 * bannières masquées. Ce ne sont pas des préférences de fond (thème, poste) —
 * juste éviter d'avoir à re-cliquer la même croix à chaque navigation.
 *
 * Stockage best-effort : si `localStorage` est indisponible (mode privé,
 * quota, jsdom sans persistance) on retombe silencieusement sur la valeur par
 * défaut, sans jamais lever.
 *
 * Clés `sentinelle.*` (renommage produit) — la lecture retombe sur l'ancienne
 * clé `maestro.*` et migre silencieusement (voir `legacyStorage.ts`) : un
 * poste qui avait déjà des réglages enregistrés ne les perd pas.
 */
const listeners = new Set<() => void>();

function notify(): void {
  for (const l of [...listeners]) l();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  const onStorage = () => cb();
  try {
    window.addEventListener("storage", onStorage);
  } catch {
    /* pas de window (jamais en pratique côté navigateur) */
  }
  return () => {
    listeners.delete(cb);
    try {
      window.removeEventListener("storage", onStorage);
    } catch {
      /* idem */
    }
  };
}

function readRaw(key: string): string | null {
  return readWithLegacyFallback(key, legacyKeyOf(key));
}

function writeRaw(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* stockage bloqué — on garde le comportement, sans persistance */
  }
  notify();
}

/** Drapeau booléen persistant. `"1"` = vrai, tout le reste (ou absent) = `fallback`. */
export function usePersistentFlag(key: string, fallback = false): [boolean, (value: boolean) => void] {
  const value = useSyncExternalStore(
    subscribe,
    () => {
      const raw = readRaw(key);
      return raw === null ? fallback : raw === "1";
    },
    () => fallback,
  );
  const set = useCallback((next: boolean) => writeRaw(key, next ? "1" : "0"), [key]);
  return [value, set];
}

/** Chaîne persistante (ou `null` si absente). */
export function usePersistentString(key: string): [string | null, (value: string | null) => void] {
  const value = useSyncExternalStore(subscribe, () => readRaw(key), () => null);
  const set = useCallback((next: string | null) => writeRaw(key, next), [key]);
  return [value, set];
}
